import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Plug, Copy, CheckCircle, Loader2, Key, ExternalLink } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useNavigate } from 'react-router-dom';
import { mcpKeyService, MCPKey } from '../../../services/mcpKeyService';
import { subdomainApiService } from '../../../services/subdomainApiService';

export const McpNode: React.FC<{ data: { label: string; color: string; isTemplate?: boolean } }> = ({ data }) => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState<MCPKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState<'key' | 'config' | null>(null);
  const [subdomain, setSubdomain] = useState<string | undefined>();

  useEffect(() => {
    Promise.all([
      mcpKeyService.getCurrentKey().then((k) => setApiKey(k)).catch(() => {}),
      subdomainApiService.getSubdomainConfig().then((c) => setSubdomain(c?.subdomain)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const key = await mcpKeyService.generateKey();
      setApiKey(key);
    } catch {
      // ignore
    } finally {
      setGenerating(false);
    }
  };

  const maskedKey = apiKey ? apiKey.api_key.slice(0, 8) + '...' + apiKey.api_key.slice(-4) : '';
  const endpointUrl = subdomain ? subdomainApiService.getApiEndpoint(subdomain) : 'https://api.bigsplicer.com';

  const configJson = JSON.stringify(
    {
      mcpServers: {
        bigsplice: {
          url: `${endpointUrl}/mcp`,
          headers: {
            'X-API-Key': apiKey ? apiKey.api_key : '<YOUR_API_KEY>',
          },
        },
      },
    },
    null,
    2
  );

  const copyToClipboard = (text: string, which: 'key' | 'config') => {
    navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[400px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <Plug className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && !apiKey && (
          <div className="space-y-2">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
              MCP API Key
            </p>
            <p className="text-xs text-muted-foreground">No key generated yet</p>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs h-7"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Key className="h-3 w-3 mr-1" />
              )}
              Generate Key
            </Button>
          </div>
        )}

        {!loading && apiKey && (
          <>
            {/* Key display */}
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                MCP API Key
              </p>
              <div className="flex items-center gap-1.5">
                <Badge variant="secondary" className="text-[10px] font-mono px-1.5">
                  <Key className="h-2.5 w-2.5 mr-1" />
                  {maskedKey}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => copyToClipboard(apiKey.api_key, 'key')}
                >
                  {copied === 'key' ? (
                    <CheckCircle className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>

            {/* Claude Desktop config */}
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                Claude Desktop Config
              </p>
              <div className="relative nodrag nowheel">
                <pre className="text-[10px] font-mono bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-[120px] leading-relaxed">
                  {configJson}
                </pre>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 absolute top-1 right-1"
                  onClick={() => copyToClipboard(configJson, 'config')}
                >
                  {copied === 'config' ? (
                    <CheckCircle className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}

        <Button
          variant="link"
          size="sm"
          className="text-[11px] p-0 h-auto"
          onClick={() => navigate('/developer')}
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          Open Developer Page
        </Button>
      </div>

      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
    </div>
  );
};
